import { storedHeadOf } from "../edit-head";
import {
  copyHeadOf,
  CopyHead,
  LockReason,
  lockedCopyDescriptionOf,
  lockedCopyNoteOf,
  lostHeadNoteOf,
} from "../lock-reason";
import { safeCalcQuote } from "../safe-calc";
import { calcInputOf } from "./reducer";
import { sheetToTsv } from "./sheet/grid-model";
import { EditorState } from "./types";

export interface CopyEditsInput {
  state: EditorState;
  spaces?: { code: string; label: string }[];
  reason: LockReason;
  /** 마지막 서버 저장본의 머리 정보 — 새 버전이 이것을 복사해 간다. */
  savedHead: CopyHead;
}

export interface CopyEditsDeps {
  writeText: (text: string) => Promise<void>;
  success: (title: string, opts: { description: string }) => void;
  error: (message: string) => void;
}

// 화면 머리를 서버 머리(lock_head)와 같은 자로 — 저장했다면 DB 에 남을 모양(절사 제외 총액으로 자동 절사 판정, 못 세면 null).
export const stateHeadOf = (state: EditorState): CopyHead => {
  const calc = safeCalcQuote({ ...calcInputOf(state), rounding_adjustment: 0 });
  return storedHeadOf(state, calc.ok ? calc.result.gross : null);
};

// 잠긴 견적의 화면 편집을 시트 붙여넣기용 TSV 로 복사 — writeText 가 첫 await 라 클릭 제스처 안에서 불린다.
export const copyLockedEdits = async (input: CopyEditsInput, deps: CopyEditsDeps): Promise<boolean> => {
  const text = sheetToTsv(input.state, { spaces: input.spaces, exprs: true });
  try {
    await deps.writeText(text);
  } catch {
    deps.error("복사하지 못했습니다 — 시트에서 전체 선택 후 복사해 주세요");
    return false;
  }
  const headNote = lostHeadNoteOf(stateHeadOf(input.state), input.savedHead);
  deps.success(lockedCopyNoteOf(input.reason), {
    description: lockedCopyDescriptionOf(input.reason, headNote),
  });
  return true;
};

export interface LockedCopySession {
  savedHead: () => CopyHead;
  saved: (sent: EditorState) => void;
  locked: (serverHead?: CopyHead) => void;
  copy: (input: Omit<CopyEditsInput, "savedHead">, deps: CopyEditsDeps) => Promise<boolean>;
}

// 복사 비교 기준(새 버전이 복사해 갈 머리) — 첫 화면(저장본)에서 잡고, 저장 성공 때 보낸 상태로, 잠김 응답이 서버 머리를 주면 그것으로 갈아 끼운다.
export const lockedCopySession = (initial: EditorState): LockedCopySession => {
  let savedHead = copyHeadOf(stateHeadOf(initial));
  return {
    savedHead: () => savedHead,
    saved: (sent) => {
      savedHead = copyHeadOf(stateHeadOf(sent));
    },
    locked: (serverHead) => {
      if (serverHead) savedHead = copyHeadOf(serverHead);
    },
    copy: (input, deps) => copyLockedEdits({ ...input, savedHead }, deps),
  };
};
